import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, ArrowUpRight, Linkedin, Twitter, Youtube, Send } from "lucide-react";
import { LOGO_URL } from "@/lib/data";
import { postWebsiteLead } from "@/lib/leadCapture";
import { sanitizeNameInput } from "@/lib/formInput";
import PhoneInput, { isValidPhone } from "@/components/ui-shared/PhoneInput";


const linkGroups = [
    {
        title: "Products",
        links: [
            { label: "Enterprise LxP", to: "/products/enterprise-lxp" },
            { label: "eLearning", to: "/products/elearning" },
            { label: "Cloud Labs", to: "/products/cloud-labs" },
            { label: "AI Assessments", to: "/products/ai-assessments" },
            { label: "AI Coding Platform", to: "/products/ai-coding" },
        ],
    },
    {
        title: "Solutions",
        links: [
            { label: "Corporate Training", to: "/solutions/corporate-training" },
            { label: "New Hire Training", to: "/solutions/new-hire-training" },
            { label: "Certifications", to: "/solutions/certifications" },
            { label: "Managed L&D", to: "/solutions/managed-ld" },
            { label: "Hire–Train–Deploy", to: "/solutions/hire-train-deploy" },
        ],
    },
    {
        title: "Learning Tracks",
        links: [
            { label: "GenAI", to: "/learning-tracks" },
            { label: "Data, Analytics & AI", to: "/learning-tracks" },
            { label: "Cloud & DevOps", to: "/learning-tracks" },
            { label: "Cybersecurity", to: "/learning-tracks" },
            { label: "Software Engineering", to: "/learning-tracks" },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "Partners", to: "/partners" },
            { label: "All Products", to: "/products" },
            { label: "All Solutions", to: "/solutions" },
            { label: "About SpringXE", to: "/about" },
            { label: "Contact", to: "#contact" },
        ],
    },
];

const Footer = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        interest: "Enterprise LxP",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
            toast.error("Please fill in all the fields.");
            return;
        }
        if (!isValidPhone(form.phone)) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        try {
            const ok = await postWebsiteLead(form, "contact");
            if (ok) {
                toast.success("Thanks. Our enterprise team will reach out shortly.");
                setForm({ name: "", email: "", company: "", phone: "", interest: "Enterprise LxP", message: "" });
            } else {
                toast.error("Unable to submit right now. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer id="contact" data-testid="site-footer" className="relative overflow-hidden">
            {/* Contact block */}
            <div className="relative">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="hero-orb w-[500px] h-[500px] bg-[#A100FF]/20 -top-40 -left-20" />
                    <div className="hero-orb w-[400px] h-[400px] bg-[#7B00CC]/15 top-20 right-10" />
                </div>

                <div className="sxe-container sxe-section relative">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                        {/* Left: pitch */}
                        <div>
                            <div className="overline mb-5">
                                <span className="dot" /> Talk to Our Experts
                            </div>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white">
                                Ready to build your{" "}
                                <span className="text-gradient">future-ready workforce</span>?
                            </h2>
                            <p className="mt-6 text-[#B0A3C4] text-lg leading-relaxed max-w-xl">
                                Share a few details and our enterprise team will design a program aligned to your business outcomes — usually within one working day.
                            </p>

                            <div className="mt-10 grid gap-4 max-w-md">
                                <a
                                    href="mailto:info@springxe.com"
                                    data-testid="footer-email-link"
                                    className="sxe-card p-5 flex items-center gap-4"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-[#A100FF]/15 border border-[#C266FF]/25 flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-[#E9CCFF]" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#75668C] font-display">Email</div>
                                        <div className="text-white font-display font-medium">info@springxe.com</div>
                                    </div>
                                    <ArrowUpRight className="w-4 h-4 text-[#B0A3C4] ml-auto" />
                                </a>
                                <div className="sxe-card p-5 flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-[#A100FF]/15 border border-[#C266FF]/25 flex items-center justify-center shrink-0">
                                        <MapPin className="w-5 h-5 text-[#E9CCFF]" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#75668C] font-display">Bangalore, India</div>
                                        <div className="text-white text-sm leading-relaxed mt-0.5">
                                            WeWork Vaishnavi Signature, No. 78/9, Outer Ring Road, Bellandur Village, Varthur Hobli, Bangalore, Karnataka – 560103
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: form */}
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            className="sxe-form-card rounded-2xl p-6 md:p-8"
                        >
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Full Name*</label>
                                    <input
                                        data-testid="contact-input-name"
                                        name="name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: sanitizeNameInput(e.target.value) })}
                                        className="sxe-form-input mt-1"
                                        placeholder="Your full name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Work Email*</label>
                                    <input
                                        data-testid="contact-input-email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        className="sxe-form-input mt-1"
                                        placeholder="you@company.com"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Company*</label>
                                    <input
                                        data-testid="contact-input-company"
                                        name="company"
                                        value={form.company}
                                        onChange={onChange}
                                        className="sxe-form-input mt-1"
                                        placeholder="Company name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Phone*</label>
                                    <PhoneInput testId="contact-input-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Interest</label>
                                    <select
                                        data-testid="contact-input-interest"
                                        name="interest"
                                        value={form.interest}
                                        onChange={onChange}
                                        className="sxe-form-input mt-1"
                                    >
                                        <option>Enterprise LxP</option>
                                        <option>Corporate Training</option>
                                        <option>Hire–Train–Deploy</option>
                                        <option>AI Assessments</option>
                                        <option>Cloud Labs</option>
                                        <option>Managed L&amp;D</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-widest text-[#6B5980] font-display">Message</label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        rows={4}
                                        className="sxe-form-input mt-1 resize-none"
                                        placeholder="Tell us about your capability goals..."
                                    />
                                </div>
                            </div>

                            <button
                                data-testid="contact-submit-btn"
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full mt-6 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? "Submitting..." : (<>Talk to Our Experts <Send className="w-4 h-4" /></>)}
                            </button>
                            <p className="text-[11px] text-[#75668C] mt-3 text-center">
                                By submitting, you agree to be contacted by SpringXE about enterprise learning solutions.
                            </p>
                        </form>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className="sxe-divider" />

            {/* Bottom footer */}
            <div className="sxe-container py-12 relative">
                <div className="grid lg:grid-cols-6 gap-10">
                    <div className="lg:col-span-2">
                        <Link to="/" data-testid="footer-logo" className="inline-flex items-center gap-3">
                            <img src={LOGO_URL} alt="SpringXE" className="h-10 w-auto" />
                        </Link>
                        <p className="text-[#B0A3C4] text-sm leading-relaxed mt-5 max-w-sm">
                            SpringXE is an Enterprise Learning &amp; Capability Development Company powering future-ready workforces through an AI-native Learning Experience Platform.
                        </p>
                        <div className="flex items-center gap-3 mt-6">
                            {[
                                { icon: Linkedin, label: "linkedin" },
                                { icon: Twitter, label: "twitter" },
                                { icon: Youtube, label: "youtube" },
                            ].map(({ icon: Icon, label }) => (
                                <a
                                    key={label}
                                    href="#"
                                    data-testid={`footer-social-${label}`}
                                    aria-label={label}
                                    className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.08] hover:border-[#C266FF]/40 hover:bg-[#A100FF]/10 flex items-center justify-center text-[#B0A3C4] hover:text-white transition-colors"
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {linkGroups.map((g) => (
                        <div key={g.title}>
                            <div className="font-display text-white text-sm font-medium tracking-tight mb-4">{g.title}</div>
                            <ul className="space-y-2.5">
                                {g.links.map((l) => (
                                    <li key={l.label}>
                                        <Link
                                            to={l.to}
                                            data-testid={`footer-link-${l.label.toLowerCase().replace(/\W+/g, "-")}`}
                                            className="text-[13px] text-[#B0A3C4] hover:text-white transition-colors"
                                        >
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="sxe-divider my-10" />

                <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-6" data-testid="footer-bottom-bar">
                    <div className="flex flex-col md:flex-row items-center md:items-center gap-4 md:gap-6 order-2 md:order-1">
                        <div className="text-xs text-[#75668C] text-center md:text-left">
                            © {new Date().getFullYear()} SpringXE. All rights reserved.
                        </div>
                        <div className="text-xs text-[#75668C] text-center md:text-left">
                            Xelerating Careers. Building Futures.
                        </div>
                    </div>

                    {/* D-U-N-S® Registered™ trust badges */}
                    <div
                        className="order-1 md:order-2 flex flex-col md:flex-row items-center justify-center md:justify-end gap-3 p-4 md:py-2 md:px-4"
                        data-testid="footer-duns-seal"
                    >
                        <iframe
                            title="D-U-N-S Registered Seal"
                            src="https://dunsregistered.dnb.com/SealAuthentication.aspx?Cid=1"
                            width="114"
                            height="97"
                            frameBorder="0"
                            scrolling="no"
                            allowTransparency="true"
                            loading="lazy"
                            style={{ border: 0, background: "transparent" }}
                        />
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
